import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import {
  fetchTemplatesApi,
  createTemplateApi,
  deleteTemplateApi,
  patchTemplateApi,
} from '../Email-Marketing/shared/api';

// Replaces the Email-Marketing `useTemplatesStore` zustand store.

const normalizeTemplate = (raw = {}) => {
  const metrics = raw.metrics || {};
  return {
    ...raw,
    id: raw._id || raw.id,
    title: raw.title || raw.name || 'Untitled',
    content: raw.content || raw.htmlContent || raw.textContent || '',
    // Subject is the canonical field; previewText is the pre-rename name and is kept
    // populated so list/card views that still read it keep working.
    subject: raw.subject || raw.previewText || '',
    previewText: raw.previewText || raw.subject || '',
    category: raw.category || 'Uncategorized',
    attachments: raw.attachments || [],
    favorite: Boolean(raw.favorite),
    lastUsed: raw.lastUsed || null,
    usageCount: raw.usageCount || 0,
    opens: (metrics.opens ?? metrics.opened ?? raw.opens) || 0,
    clicks: (metrics.clicks ?? metrics.clicked ?? raw.clicks) || 0,
    replies: (metrics.replies ?? metrics.replied ?? raw.replies) || 0,
    blocked: (metrics.blocked ?? metrics.complaints ?? raw.blocked) || 0,
    bounces: (metrics.bounces ?? metrics.bounced ?? raw.bounces) || 0,
    sent: (metrics.sent ?? raw.sent) || 0,
    delivered: (metrics.delivered ?? raw.delivered) || 0,
    // Denominator for the Delivered column: the audience the template's campaigns
    // set out to reach. `delivered` above already existed.
    totalRecipients: (metrics.totalRecipients ?? raw.totalRecipients) || 0,
    openRate: (metrics.openRate ?? raw.openRate) || 0,
    clickRate: (metrics.clickRate ?? raw.clickRate) || 0,
    replyRate: (metrics.replyRate ?? raw.replyRate) || 0,
    bounceRate: (metrics.bounceRate ?? raw.bounceRate) || 0,
    blockRate: (metrics.blockRate ?? raw.blockRate) || 0,
  };
};

// These two were exposed through the zustand store but are pure helpers with no
// state involvement, so they are plain exports now.
export const getTemplateCategories = () =>
  Array.from(new Set(['Newsletter', 'Promotion', 'Announcement', 'Transactional', 'Other']));

export const processDynamicContent = (content, variables = {}) => {
  let processedContent = content;

  const defaultVars = {
    firstName: 'John',
    lastName: 'Doe',
    email: 'john.doe@example.com',
    companyName: 'Your Company',
    currentDate: new Date().toLocaleDateString("en-GB"),
    unsubscribeLink: '#unsubscribe',
  };

  const allVars = { ...defaultVars, ...variables };

  Object.keys(allVars).forEach((key) => {
    const regex = new RegExp(`{{${key}}}`, 'g');
    processedContent = processedContent.replace(regex, allVars[key]);
  });

  return processedContent;
};

export const loadTemplates = createAsyncThunk(
  'emailTemplates/loadTemplates',
  async (_, { rejectWithValue }) => {
    try {
      const list = await fetchTemplatesApi();
      const normalized = (list || []).map(normalizeTemplate);

      // Deduplicate templates by ID
      const uniqueTemplates = [];
      const seenIds = new Set();
      for (const t of normalized) {
        if (t.id && !seenIds.has(t.id)) {
          seenIds.add(t.id);
          uniqueTemplates.push(t);
        }
      }
      return uniqueTemplates;
    } catch (e) {
      console.error('Failed to load templates:', e);
      return rejectWithValue(e.message);
    }
  }
);

export const addTemplate = createAsyncThunk(
  'emailTemplates/addTemplate',
  async (t) => {
    const payload = {
      title: t.title,
      subject: t.subject || t.previewText || '',
      previewText: t.previewText || t.subject || '',
      category: t.category,
      content: t.content,
      attachments: t.attachments || [],
      favorite: !!t.favorite,
      campaignId: t.campaignId,
    };
    const created = await createTemplateApi(payload);
    return created;
  }
);

export const updateTemplate = createAsyncThunk(
  'emailTemplates/updateTemplate',
  async ({ id, patch }) => {
    // PATCH, not PUT: callers send partial patches ({ campaignId } to link a template to a
    // campaign). PUT runs the full-template validator, which rejects anything without a title
    // and content -- every link failed with 400 and was swallowed, so Content tabs stayed empty.
    // PATCH applies only whitelisted fields (templateController.patchTemplate).
    const updated = await patchTemplateApi(id, patch);
    return { id, updated };
  }
);

export const removeTemplate = createAsyncThunk(
  'emailTemplates/removeTemplate',
  async (id) => {
    await deleteTemplateApi(id);
    return id;
  }
);

export const toggleFavorite = createAsyncThunk(
  'emailTemplates/toggleFavorite',
  async (id, { getState }) => {
    const current = getState().emailTemplates.templates.find(
      (t) => t.id === id || t._id === id
    );
    if (!current) return null;
    const updated = await patchTemplateApi(id, { favorite: !current.favorite });
    return { id, updated };
  }
);

const initialState = {
  templates: [],
  loading: false,
  error: null,
};

const emailTemplatesSlice = createSlice({
  name: 'emailTemplates',
  initialState,
  reducers: {
    setTemplates: (state, action) => {
      state.templates = action.payload;
    },
    clearTemplatesError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    const replaceOne = (state, action) => {
      if (!action.payload) return;
      const { id, updated } = action.payload;
      const normalized = normalizeTemplate(updated);
      state.templates = state.templates.map((x) =>
        x.id === id || x._id === id ? normalized : x
      );
    };
    builder
      .addCase(loadTemplates.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(loadTemplates.fulfilled, (state, action) => {
        state.loading = false;
        state.templates = action.payload;
      })
      .addCase(loadTemplates.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(addTemplate.fulfilled, (state, action) => {
        state.templates = [normalizeTemplate(action.payload), ...state.templates];
      })
      .addCase(updateTemplate.fulfilled, replaceOne)
      .addCase(toggleFavorite.fulfilled, replaceOne)
      .addCase(removeTemplate.fulfilled, (state, action) => {
        state.templates = state.templates.filter(
          (x) => x.id !== action.payload && x._id !== action.payload
        );
      });
  },
});

export const { setTemplates, clearTemplatesError } = emailTemplatesSlice.actions;

export default emailTemplatesSlice.reducer;
